import React, { useMemo } from 'react';
import {
  type ButtonType,
  CellField,
  DataTableRenderCellParams,
  type ErpInputTableActions,
  ErpInputTableV2,
  GridEventListener
} from '@/components/custom/erp';
import type { SelectedCellInfo } from '@/hooks/use-input-table-rows';
import { dvtSearchColumns, QUERY_KEYS } from '@/constants';
import { DonViTinh, DonViTinhPhu } from '@/types/schemas';
import { SearchField } from '@/components/custom/erp';
import { formatNumber } from '@/lib/formatUtils';

interface UnitConversionTabProps {
  mode: 'add' | 'edit' | 'view';
  rows: DonViTinhPhu[];
  selectedRowUuid?: string | null;
  selectedCell?: SelectedCellInfo | null;
  onRowClick?: GridEventListener<'rowClick'>;
  onCellClick?: (params: DataTableRenderCellParams) => void;
  onAddRow: () => void;
  onDeleteRow: () => void;
  onCopyRow: () => void;
  onPasteRow: () => void;
  onMoveRow: (direction: 'up' | 'down') => void;
  onCellValueChange: (rowUuid: string, field: string, newValue: DonViTinhPhu[keyof DonViTinhPhu]) => void;
  dvt: DonViTinh | null;
}

const UnitConversionTab: React.FC<UnitConversionTabProps> = ({
  mode,
  rows,
  selectedRowUuid,
  onRowClick,
  onAddRow,
  onDeleteRow,
  onCopyRow,
  onPasteRow,
  onMoveRow,
  onCellValueChange,
  dvt
}) => {
  const actions = useMemo<ErpInputTableActions>(
    () => ({
      add: onAddRow,
      delete: onDeleteRow,
      copy: onCopyRow,
      paste: onPasteRow,
      moveUp: () => onMoveRow('up'),
      moveDown: () => onMoveRow('down'),
      export: () => {},
      pin: () => {}
    }),
    [onAddRow, onCopyRow, onDeleteRow, onMoveRow, onPasteRow]
  );

  const actionOrder = useMemo<ButtonType[]>(
    () => ['add', 'delete', 'copy', 'paste', 'moveUp', 'moveDown', 'export', 'pin'],
    []
  );

  return (
    <ErpInputTableV2
      autoHeight
      rows={rows}
      onRowClick={onRowClick}
      selectedRowId={selectedRowUuid || undefined}
      columns={[
        {
          field: 'dvt',
          headerName: 'Đvt',
          width: 150,
          renderCell: params => (
            <SearchField<DonViTinh>
              searchEndpoint={`/${QUERY_KEYS.DON_VI_TINH}`}
              searchColumns={dvtSearchColumns}
              columnDisplay='ten_dvt'
              dialogTitle='Danh mục đơn vị tính'
              value={params.row.dvt_data?.ten_dvt}
              onRowSelection={(row: DonViTinh) => onCellValueChange(params.row.uuid, 'dvt_data', row)}
              disabled={!dvt}
            />
          )
        },
        {
          field: 'he_so',
          headerName: 'Hệ số',
          width: 250,
          renderCell: params => (
            <div className='flex items-center justify-between gap-x-2'>
              <CellField
                name='he_so'
                type='number'
                className='flex-1'
                disabled={!params.row.dvt_data}
                placeholder='0.00'
                value={params.row.he_so}
                onValueChange={newValue => onCellValueChange(params.row.uuid, 'he_so', newValue)}
              />

              {params.row.he_so && (
                <span className='w-28 text-right text-[11px] font-semibold text-green-600'>
                  {`1 (${params.row.dvt_data?.ten_dvt}) = ${formatNumber(params.row.he_so)} (${dvt?.ten_dvt})`}
                </span>
              )}
            </div>
          )
        }
      ]}
      getRowId={row => row?.uuid || ''}
      actions={actions}
      actionOrder={actionOrder}
      actionsDisabled={mode === 'view'}
      showIndexColumn
    />
  );
};

export default UnitConversionTab;
